// app/javascript/controllers/friends_picker_controller.js
// Custom friends picker with search, multi-selection, and hidden inputs for form submission
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "selected", "hiddenContainer"]
  static values = {
    url: { type: String, default: "/friends.json" },
    fieldName: { type: String, default: "tagged_user_ids" },
    modelName: { type: String, default: "media" }
  }

  connect() {
    this.selectedFriends = new Map() // id -> { id, display_name, avatar_url }
    this.debounceTimer = null
    this.abortController = null

    // Pre-populate from existing hidden inputs if any
    this.preloadSelected()

    // Close results when clicking outside
    this.boundCloseResults = this.closeResults.bind(this)
    document.addEventListener("click", this.boundCloseResults)
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (this.abortController) this.abortController.abort()
    document.removeEventListener("click", this.boundCloseResults)
  }

  preloadSelected() {
    // Check for existing hidden inputs with pre-selected friends data
    const existingInputs = this.hiddenContainerTarget.querySelectorAll("input[type=hidden]")
    existingInputs.forEach(input => {
      const id = input.value
      const displayName = input.dataset.displayName || `Friend ${id}`
      const avatarUrl = input.dataset.avatarUrl || null
      if (id && !this.selectedFriends.has(id)) {
        this.selectedFriends.set(id, { id, display_name: displayName, avatar_url: avatarUrl })
      }
    })
    this.renderSelected()
  }

  search(event) {
    const query = event.target.value.trim()

    // Clear previous timer
    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    // Hide results if query is too short
    if (query.length < 2) {
      this.hideResults()
      return
    }

    // Debounce the search
    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query)
    }, 250)
  }

  async fetchResults(query) {
    // Abort any pending request
    if (this.abortController) {
      this.abortController.abort()
    }
    this.abortController = new AbortController()

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("search", query)

      const response = await fetch(url.toString(), {
        signal: this.abortController.signal,
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Network response was not ok")

      const friends = await response.json()
      this.renderResults(friends)
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Friends search error:", error)
        this.hideResults()
      }
    }
  }

  renderResults(friends) {
    // Filter out already selected friends
    const filteredFriends = friends.filter(f => !this.selectedFriends.has(String(f.id)))

    if (filteredFriends.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="p-3 text-sm text-gray-500 dark:text-gray-400 text-center">
          Aucun résultat
        </div>
      `
      this.showResults()
      return
    }

    const html = filteredFriends.map(friend => `
      <button type="button"
              class="w-full flex items-center gap-3 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
              data-action="click->friends-picker#selectFriend"
              data-friend-id="${friend.id}"
              data-friend-name="${this.escapeHtml(friend.display_name)}"
              data-friend-avatar="${friend.avatar_url || ""}">
        ${this.renderAvatar(friend, 8)}
        <span class="text-sm font-medium text-gray-900 dark:text-white truncate">
          ${this.escapeHtml(friend.display_name)}
        </span>
      </button>
    `).join("")

    this.resultsTarget.innerHTML = html
    this.showResults()
  }

  selectFriend(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const id = String(button.dataset.friendId)
    const displayName = button.dataset.friendName
    const avatarUrl = button.dataset.friendAvatar || null

    // Prevent duplicates
    if (this.selectedFriends.has(id)) return

    // Add to selection
    this.selectedFriends.set(id, {
      id,
      display_name: displayName,
      avatar_url: avatarUrl
    })

    // Clear input and hide results
    this.inputTarget.value = ""
    this.hideResults()

    // Re-render selected friends and sync hidden inputs
    this.renderSelected()
    this.syncHiddenInputs()
  }

  removeFriend(event) {
    event.preventDefault()
    const id = String(event.currentTarget.dataset.friendId)

    this.selectedFriends.delete(id)
    this.renderSelected()
    this.syncHiddenInputs()
  }

  renderSelected() {
    if (this.selectedFriends.size === 0) {
      this.selectedTarget.innerHTML = ""
      return
    }

    const html = Array.from(this.selectedFriends.values()).map(friend => `
      <div class="inline-flex items-center gap-1.5 px-2 py-1 bg-sky-100 dark:bg-sky-900/30 rounded-full text-sm">
        ${this.renderAvatar(friend, 5)}
        <span class="text-sky-800 dark:text-sky-200 truncate max-w-32">
          ${this.escapeHtml(friend.display_name)}
        </span>
        <button type="button"
                class="ml-1 text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-200 transition-colors"
                data-action="click->friends-picker#removeFriend"
                data-friend-id="${friend.id}"
                aria-label="Retirer ${this.escapeHtml(friend.display_name)}">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `).join("")

    this.selectedTarget.innerHTML = html
  }

  syncHiddenInputs() {
    // Generate hidden inputs for Rails form submission
    // Using the pattern: model[field_name][]
    const inputName = `${this.modelNameValue}[${this.fieldNameValue}][]`

    const html = Array.from(this.selectedFriends.values()).map(friend => `
      <input type="hidden"
             name="${inputName}"
             value="${friend.id}"
             data-display-name="${this.escapeHtml(friend.display_name)}"
             data-avatar-url="${friend.avatar_url || ""}">
    `).join("")

    this.hiddenContainerTarget.innerHTML = html
  }

  showResults() {
    this.resultsTarget.classList.remove("hidden")
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden")
  }

  closeResults(event) {
    // Close dropdown when clicking outside
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  renderAvatar(friend, size) {
    if (friend.avatar_url) {
      return `<img src="${friend.avatar_url}"
                   alt="${this.escapeHtml(friend.display_name)}"
                   class="w-${size} h-${size} rounded-full object-cover bg-gray-200 dark:bg-gray-600 shrink-0">`
    }

    // Generate initials fallback
    const initials = this.getInitials(friend.display_name)
    return `<span class="inline-flex shrink-0 justify-center items-center w-${size} h-${size} rounded-full bg-sky-500 text-white text-xs font-semibold">
              ${initials}
            </span>`
  }

  getInitials(name) {
    if (!name) return "?"
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }
}

